import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Shield,
  KeyRound,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Send,
  Save,
  RotateCcw,
  Sparkles,
  User,
  Briefcase,
  GraduationCap,
  Globe,
  Plus,
  Trash2,
  Lock,
  ChevronRight,
  Loader2,
} from "lucide-react";

const API_BASE = "/api/candidate-cv";

export default function CandidateCvInvitationPage() {
  const { token: urlToken } = useParams();
  const navigate = useNavigate();

  // Security: Keep token in memory, avoid leaking in storage
  const [invitationToken, setInvitationToken] = useState(urlToken || "");
  const [sessionToken, setSessionToken] = useState(() => sessionStorage.getItem("lenvia_cand_session") || "");

  // Journey steps: "request_otp" | "verify_otp" | "editing" | "completed" | "error"
  const [step, setStep] = useState(sessionToken ? "editing" : "request_otp");

  // OTP Form State
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // CV Editor State
  const [draftId, setDraftId] = useState("");
  const [revision, setRevision] = useState(1);
  const [cvData, setCvData] = useState({
    basics: { full_name: "", title: "", summary: "", email: "", phone: "" },
    skills: [],
    experiences: [],
    education: [],
    languages: [],
  });
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [remainingMinutes, setRemainingMinutes] = useState(null);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle"); // "idle" | "saving" | "saved" | "conflict" | "error"
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [activeTab, setActiveTab] = useState("basics");

  // Modals & Errors
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeMessage, setCompleteMessage] = useState("");
  const [isCompleting, setIsCompleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorType, setErrorType] = useState(""); // "expired" | "revoked" | "ratelimit" | "generic"

  // Autosave debouncing ref
  const autosaveTimerRef = useRef(null);
  const isDirtyRef = useRef(false);

  // 1. Sanitize URL to avoid token leaking in referrers or browser history
  useEffect(() => {
    if (urlToken) {
      setInvitationToken(urlToken);
      // Clean token from address bar for referrer protection
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, "", "/candidat/cv/session");
      }
    }
  }, [urlToken]);

  // 2. Countdown timer for session expiration
  useEffect(() => {
    if (!sessionExpiresAt) return;
    const interval = setInterval(() => {
      const diffMs = new Date(sessionExpiresAt).getTime() - Date.now();
      if (diffMs <= 0) {
        setRemainingMinutes(0);
        setErrorType("expired");
        setErrorMessage("Votre session a expiré. Veuillez demander un nouveau code d'accès.");
        setStep("error");
        sessionStorage.removeItem("lenvia_cand_session");
        clearInterval(interval);
      } else {
        setRemainingMinutes(Math.ceil(diffMs / 60000));
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [sessionExpiresAt]);

  // -------------------------------------------------------------------------
  // Step 1: Request OTP
  // -------------------------------------------------------------------------
  const handleRequestOtp = async (e) => {
    if (e) e.preventDefault();
    setIsRequestingOtp(true);
    setErrorMessage("");

    try {
      const res = await fetch(`${API_BASE}/access/request-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          invitation_token: invitationToken,
          email: email.trim() || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        if (res.status === 410) {
          setErrorType("expired");
          setErrorMessage(data.detail || "Ce lien d'invitation a expiré ou a été révoqué.");
          setStep("error");
          return;
        }
        if (res.status === 429) {
          setErrorType("ratelimit");
          setErrorMessage(data.detail || "Trop de tentatives. Veuillez patienter avant de réessayer.");
          return;
        }
        throw new Error(data.detail || "Erreur lors de la demande d'OTP.");
      }

      setMaskedEmail(data.masked_email || "votre adresse email");
      setOtpExpiresAt(data.expires_at);
      setAttemptsRemaining(data.attempts_remaining ?? 3);
      setStep("verify_otp");
    } catch (err) {
      setErrorMessage(err.message || "Impossible de demander le code.");
    } finally {
      setIsRequestingOtp(false);
    }
  };

  // -------------------------------------------------------------------------
  // Step 2: Verify OTP
  // -------------------------------------------------------------------------
  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      setErrorMessage("Veuillez saisir le code complet à 6 chiffres.");
      return;
    }

    setIsVerifyingOtp(true);
    setErrorMessage("");

    try {
      const res = await fetch(`${API_BASE}/access/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          invitation_token: invitationToken,
          otp: otpCode.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        if (res.status === 410) {
          setErrorType("expired");
          setErrorMessage(data.detail || "Cette invitation n'est plus valide.");
          setStep("error");
          return;
        }
        if (res.status === 429) {
          setErrorType("ratelimit");
          setErrorMessage(data.detail || "Nombre maximal de tentatives atteint.");
          return;
        }
        throw new Error(data.detail || "Code de vérification invalide.");
      }

      // Save token securely in session storage
      setSessionToken(data.access_token);
      sessionStorage.setItem("lenvia_cand_session", data.access_token);
      setSessionExpiresAt(data.expires_at);
      setDraftId(data.working_cv_draft_id);

      // Transition to editing
      setStep("editing");
      loadDraft(data.access_token);
    } catch (err) {
      setErrorMessage(err.message || "Erreur de validation du code.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // -------------------------------------------------------------------------
  // Step 3: Fetch Draft
  // -------------------------------------------------------------------------
  const loadDraft = useCallback(async (tokenToUse) => {
    const authHeader = `Bearer ${tokenToUse || sessionToken}`;
    setIsLoadingDraft(true);
    setErrorMessage("");

    try {
      const res = await fetch(`${API_BASE}/draft`, {
        headers: { Authorization: authHeader },
      });

      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401 || res.status === 410) {
          setErrorType("expired");
          setErrorMessage("Votre session a expiré.");
          setStep("error");
          return;
        }
        throw new Error(data.detail || "Impossible de charger le CV.");
      }

      const cv = data.cv || {};
      setCvData({
        basics: cv.basics || { full_name: "", title: "", summary: "", email: "", phone: "" },
        skills: Array.isArray(cv.skills) ? cv.skills : [],
        experiences: Array.isArray(cv.experiences) ? cv.experiences : [],
        education: Array.isArray(cv.education || cv.educations) ? (cv.education || cv.educations) : [],
        languages: Array.isArray(cv.languages) ? cv.languages : [],
      });
      setRevision(data.revision || 1);
      setDraftId(data.draft_id);
      if (data.expires_at) {
        setSessionExpiresAt(data.expires_at);
      }
      setSaveStatus("saved");
      setLastSavedAt(new Date());
    } catch (err) {
      setErrorMessage(err.message || "Erreur de chargement du document.");
    } finally {
      setIsLoadingDraft(false);
    }
  }, [sessionToken]);

  // Load draft on mount if session exists
  useEffect(() => {
    if (sessionToken && step === "editing") {
      loadDraft(sessionToken);
    }
  }, [sessionToken, step, loadDraft]);

  // -------------------------------------------------------------------------
  // Step 4: Autosave (Guarded Mutation with expected_revision)
  // -------------------------------------------------------------------------
  const triggerAutosave = useCallback((patchData, currentRev) => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    setSaveStatus("saving");

    autosaveTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/draft`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionToken}`,
          },
          body: JSON.stringify({
            expected_revision: currentRev,
            patch: patchData,
          }),
        });

        const result = await res.json();

        if (res.status === 409) {
          setSaveStatus("conflict");
          setErrorMessage("Conflit de modification : une mise à jour a eu lieu. Veuillez rafraîchir.");
          return;
        }

        if (!res.ok) {
          throw new Error(result.detail || "Erreur de sauvegarde");
        }

        setRevision(result.revision);
        setSaveStatus("saved");
        setLastSavedAt(new Date());
        isDirtyRef.current = false;
      } catch (err) {
        setSaveStatus("error");
        setErrorMessage(err.message || "Erreur d'enregistrement.");
      }
    }, 800); // 800ms debounce
  }, [sessionToken]);

  // Update helper
  const updateSection = (section, newContent) => {
    setCvData((prev) => {
      const next = { ...prev, [section]: newContent };
      triggerAutosave({ [section]: newContent }, revision);
      return next;
    });
  };

  // -------------------------------------------------------------------------
  // Step 5: Complete Contribution ("J'ai terminé")
  // -------------------------------------------------------------------------
  const handleComplete = async () => {
    setIsCompleting(true);
    setErrorMessage("");

    try {
      const res = await fetch(`${API_BASE}/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          message: completeMessage.trim() || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Impossible de clore votre contribution.");
      }

      sessionStorage.removeItem("lenvia_cand_session");
      setShowCompleteModal(false);
      setStep("completed");
    } catch (err) {
      setErrorMessage(err.message || "Erreur lors de la clôture.");
    } finally {
      setIsCompleting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render: Error Screen
  // -------------------------------------------------------------------------
  if (step === "error") {
    return (
      <div className="max-w-md mx-auto my-12 p-8 rounded-2xl bg-[#14183a]/90 border border-slate-700/60 shadow-2xl text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400">
          <AlertTriangle className="w-7 h-7" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Accès non disponible</h2>
        <p className="text-slate-300 text-sm mb-6 leading-relaxed">{errorMessage}</p>
        <button
          onClick={() => {
            sessionStorage.removeItem("lenvia_cand_session");
            setSessionToken("");
            setStep("request_otp");
          }}
          className="px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-medium text-sm transition-all"
        >
          Recommencer avec un nouveau code
        </button>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: Completed Confirmation Screen
  // -------------------------------------------------------------------------
  if (step === "completed") {
    return (
      <div className="max-w-lg mx-auto my-12 p-8 rounded-2xl bg-[#14183a]/90 border border-emerald-500/30 shadow-2xl text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Merci pour vos modifications !</h2>
        <p className="text-slate-300 text-sm mb-6 leading-relaxed">
          Votre CV a été transmis à l'équipe de recrutement Lenvia. Votre session d'édition est désormais
          clôturée et le verrou d'accès a été libéré de façon sécurisée.
        </p>
        <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 text-xs text-slate-400">
          ✓ Sauvegarde certifiée · SHA-256 garanti · Prise en compte immédiate
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: Step 1 (Request OTP)
  // -------------------------------------------------------------------------
  if (step === "request_otp") {
    return (
      <div className="max-w-md mx-auto my-12 p-8 rounded-2xl bg-[#14183a]/90 border border-slate-700/60 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center text-violet-400">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Espace Candidat Sécurisé</h1>
            <p className="text-xs text-slate-400">Accès protégé par code OTP à 6 chiffres</p>
          </div>
        </div>

        <p className="text-sm text-slate-300 mb-6 leading-relaxed">
          Pour consulter et enrichir votre CV préparé par Lenvia, cliquez ci-dessous pour recevoir votre code
          d'authentification confidentiel.
        </p>

        {errorMessage && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleRequestOtp} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1.5">
              Adresse email associée (optionnel pour vérification)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre.email@exemple.com"
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900/80 border border-slate-700 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors"
            />
          </div>

          <button
            type="submit"
            disabled={isRequestingOtp}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-medium text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50 shadow-lg shadow-violet-600/20"
          >
            {isRequestingOtp ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <span>Recevoir mon code OTP</span>
                <Send className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 pt-4 border-t border-slate-800 text-center text-xs text-slate-500">
          Ce lien est strictement personnel, valable 72h et révocable à tout moment.
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: Step 2 (Verify OTP)
  // -------------------------------------------------------------------------
  if (step === "verify_otp") {
    return (
      <div className="max-w-md mx-auto my-12 p-8 rounded-2xl bg-[#14183a]/90 border border-slate-700/60 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center text-violet-400">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Validation du code</h1>
            <p className="text-xs text-slate-400">Code transmis à {maskedEmail}</p>
          </div>
        </div>

        <p className="text-sm text-slate-300 mb-6 leading-relaxed">
          Saisissez le code numérique à 6 chiffres reçu dans votre boîte de messagerie.
        </p>

        {errorMessage && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleVerifyOtp} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1.5">
              Code à 6 chiffres
            </label>
            <input
              type="text"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              autoFocus
              className="w-full text-center tracking-[0.5em] text-2xl font-mono px-3.5 py-3 rounded-xl bg-slate-900/80 border border-slate-700 text-white focus:outline-none focus:border-violet-500 transition-colors"
            />
          </div>

          <button
            type="submit"
            disabled={isVerifyingOtp || otpCode.length !== 6}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-medium text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50 shadow-lg shadow-violet-600/20"
          >
            {isVerifyingOtp ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <span>Accéder à mon CV</span>
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
          <button
            onClick={() => setStep("request_otp")}
            className="hover:text-violet-400 transition-colors"
          >
            ← Renvoyer un code
          </button>
          {attemptsRemaining !== null && (
            <span>Tentatives restantes : {attemptsRemaining}</span>
          )}
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: Step 3 (Interactive CV Editor)
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Top Banner: Status, Autosave, Session time, "J'ai terminé" */}
      <div className="sticky top-4 z-20 backdrop-blur-md bg-[#14183a]/90 border border-slate-700/60 rounded-2xl p-4 shadow-xl flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <Lock className="w-4 h-4 text-violet-400" />
            <span className="font-semibold text-white">Édition active</span>
            <span className="text-slate-600">·</span>
            <span>Rév. {revision}</span>
          </div>

          {/* Autosave status indicator */}
          <div className="flex items-center gap-1.5 text-xs">
            {saveStatus === "saving" && (
              <span className="text-amber-400 flex items-center gap-1">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Enregistrement...
              </span>
            )}
            {saveStatus === "saved" && (
              <span className="text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Enregistré
              </span>
            )}
            {saveStatus === "conflict" && (
              <span className="text-red-400 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                Conflit (409)
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {remainingMinutes !== null && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800 text-xs text-slate-300">
              <Clock className="w-3.5 h-3.5 text-amber-400" />
              <span>Temps restant : {remainingMinutes} min</span>
            </div>
          )}

          <button
            onClick={() => setShowCompleteModal(true)}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-medium text-xs flex items-center gap-1.5 shadow-md shadow-emerald-600/20 transition-all"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>J'ai terminé</span>
          </button>
        </div>
      </div>

      {/* Conflict / Reload Alert */}
      {saveStatus === "conflict" && (
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>Une modification concurrente a été détectée sur votre document.</span>
          </div>
          <button
            onClick={() => loadDraft(sessionToken)}
            className="px-3 py-1 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-xs font-medium flex items-center gap-1"
          >
            <RotateCcw className="w-3 h-3" />
            Recharger
          </button>
        </div>
      )}

      {/* Tabs for Candidate Sections */}
      <div className="flex border-b border-slate-800 gap-2 pb-1 overflow-x-auto">
        <button
          onClick={() => setActiveTab("basics")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            activeTab === "basics"
              ? "bg-violet-600/20 text-violet-300 border border-violet-500/30"
              : "text-slate-400 hover:text-white"
          }`}
        >
          <User className="w-4 h-4" />
          <span>Profil & Résumé</span>
        </button>
        <button
          onClick={() => setActiveTab("skills")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            activeTab === "skills"
              ? "bg-violet-600/20 text-violet-300 border border-violet-500/30"
              : "text-slate-400 hover:text-white"
          }`}
        >
          <Sparkles className="w-4 h-4" />
          <span>Compétences</span>
        </button>
        <button
          onClick={() => setActiveTab("experiences")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            activeTab === "experiences"
              ? "bg-violet-600/20 text-violet-300 border border-violet-500/30"
              : "text-slate-400 hover:text-white"
          }`}
        >
          <Briefcase className="w-4 h-4" />
          <span>Expériences</span>
        </button>
        <button
          onClick={() => setActiveTab("education")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            activeTab === "education"
              ? "bg-violet-600/20 text-violet-300 border border-violet-500/30"
              : "text-slate-400 hover:text-white"
          }`}
        >
          <GraduationCap className="w-4 h-4" />
          <span>Formations</span>
        </button>
        <button
          onClick={() => setActiveTab("languages")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            activeTab === "languages"
              ? "bg-violet-600/20 text-violet-300 border border-violet-500/30"
              : "text-slate-400 hover:text-white"
          }`}
        >
          <Globe className="w-4 h-4" />
          <span>Langues</span>
        </button>
      </div>

      {/* TAB CONTENT: Profil & Résumé */}
      {activeTab === "basics" && (
        <div className="bg-[#14183a]/90 border border-slate-700/60 rounded-2xl p-6 space-y-4">
          <h2 className="text-base font-bold text-white mb-2">Informations Générales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Nom complet</label>
              <input
                type="text"
                value={cvData.basics.full_name || ""}
                onChange={(e) =>
                  updateSection("basics", { ...cvData.basics, full_name: e.target.value })
                }
                className="w-full px-3 py-2 rounded-xl bg-slate-900/80 border border-slate-700 text-white text-sm focus:border-violet-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Titre du poste / Spécialité</label>
              <input
                type="text"
                value={cvData.basics.title || ""}
                onChange={(e) =>
                  updateSection("basics", { ...cvData.basics, title: e.target.value })
                }
                className="w-full px-3 py-2 rounded-xl bg-slate-900/80 border border-slate-700 text-white text-sm focus:border-violet-500 focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Résumé professionnel / Accroche</label>
            <textarea
              rows={4}
              value={cvData.basics.summary || ""}
              onChange={(e) =>
                updateSection("basics", { ...cvData.basics, summary: e.target.value })
              }
              placeholder="Décrivez en quelques lignes vos atouts et réalisations phares..."
              className="w-full px-3 py-2 rounded-xl bg-slate-900/80 border border-slate-700 text-white text-sm focus:border-violet-500 focus:outline-none leading-relaxed"
            />
          </div>
        </div>
      )}

      {/* TAB CONTENT: Compétences */}
      {activeTab === "skills" && (
        <div className="bg-[#14183a]/90 border border-slate-700/60 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-white">Compétences techniques & métiers</h2>
          </div>
          <p className="text-xs text-slate-400">
            Ajoutez ou modifiez vos compétences clés pour mettre en valeur votre profil auprès des recruteurs.
          </p>
          <div className="flex flex-wrap gap-2 pt-2">
            {cvData.skills.map((skill, index) => {
              const skillName = typeof skill === "string" ? skill : skill.name;
              return (
                <span
                  key={index}
                  className="px-3 py-1.5 rounded-lg bg-violet-600/20 border border-violet-500/30 text-violet-300 text-xs flex items-center gap-1.5"
                >
                  <span>{skillName}</span>
                  <button
                    onClick={() => {
                      const updated = cvData.skills.filter((_, i) => i !== index);
                      updateSection("skills", updated);
                    }}
                    className="hover:text-red-400 transition-colors"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
          {/* Add skill input */}
          <div className="flex gap-2 pt-4">
            <input
              type="text"
              id="new-skill-input"
              placeholder="Ajouter une compétence (ex: Kubernetes, TypeScript)..."
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const val = e.target.value.trim();
                  if (val && !cvData.skills.includes(val)) {
                    updateSection("skills", [...cvData.skills, val]);
                    e.target.value = "";
                  }
                }
              }}
              className="flex-1 px-3 py-2 rounded-xl bg-slate-900/80 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
            />
            <button
              onClick={() => {
                const el = document.getElementById("new-skill-input");
                if (el && el.value.trim()) {
                  updateSection("skills", [...cvData.skills, el.value.trim()]);
                  el.value = "";
                }
              }}
              className="px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Ajouter</span>
            </button>
          </div>
        </div>
      )}

      {/* TAB CONTENT: Expériences */}
      {activeTab === "experiences" && (
        <div className="bg-[#14183a]/90 border border-slate-700/60 rounded-2xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-white">Parcours professionnel</h2>
            <button
              onClick={() => {
                const newExp = {
                  title: "Nouveau poste",
                  company: "Entreprise",
                  start_date: "",
                  end_date: "",
                  description: "",
                };
                updateSection("experiences", [newExp, ...cvData.experiences]);
              }}
              className="px-3 py-1.5 rounded-lg bg-violet-600/20 border border-violet-500/30 text-violet-300 hover:bg-violet-600/30 text-xs font-medium flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Ajouter une expérience</span>
            </button>
          </div>

          <div className="space-y-4">
            {cvData.experiences.map((exp, index) => (
              <div
                key={index}
                className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 space-y-3 relative"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Poste occupé</label>
                    <input
                      type="text"
                      value={exp.title || ""}
                      onChange={(e) => {
                        const copy = [...cvData.experiences];
                        copy[index] = { ...copy[index], title: e.target.value };
                        updateSection("experiences", copy);
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-slate-950/80 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Entreprise / Client</label>
                    <input
                      type="text"
                      value={exp.company || ""}
                      onChange={(e) => {
                        const copy = [...cvData.experiences];
                        copy[index] = { ...copy[index], company: e.target.value };
                        updateSection("experiences", copy);
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-slate-950/80 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-slate-400 mb-1">Description des missions & réalisations</label>
                  <textarea
                    rows={3}
                    value={exp.description || ""}
                    onChange={(e) => {
                      const copy = [...cvData.experiences];
                      copy[index] = { ...copy[index], description: e.target.value };
                      updateSection("experiences", copy);
                    }}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950/80 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none leading-relaxed"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      const updated = cvData.experiences.filter((_, i) => i !== index);
                      updateSection("experiences", updated);
                    }}
                    className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Supprimer</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB CONTENT: Formations */}
      {activeTab === "education" && (
        <div className="bg-[#14183a]/90 border border-slate-700/60 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-white">Formations & Diplômes</h2>
            <button
              onClick={() => {
                const newEdu = { degree: "Diplôme", school: "École / Université", year: "" };
                updateSection("education", [newEdu, ...cvData.education]);
              }}
              className="px-3 py-1.5 rounded-lg bg-violet-600/20 border border-violet-500/30 text-violet-300 hover:bg-violet-600/30 text-xs font-medium flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Ajouter une formation</span>
            </button>
          </div>

          <div className="space-y-3">
            {cvData.education.map((edu, index) => (
              <div key={index} className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Diplôme</label>
                    <input
                      type="text"
                      value={edu.degree || ""}
                      onChange={(e) => {
                        const copy = [...cvData.education];
                        copy[index] = { ...copy[index], degree: e.target.value };
                        updateSection("education", copy);
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-slate-950/80 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Établissement</label>
                    <input
                      type="text"
                      value={edu.school || ""}
                      onChange={(e) => {
                        const copy = [...cvData.education];
                        copy[index] = { ...copy[index], school: e.target.value };
                        updateSection("education", copy);
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-slate-950/80 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Année d'obtention</label>
                    <input
                      type="text"
                      value={edu.year || ""}
                      onChange={(e) => {
                        const copy = [...cvData.education];
                        copy[index] = { ...copy[index], year: e.target.value };
                        updateSection("education", copy);
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-slate-950/80 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      const updated = cvData.education.filter((_, i) => i !== index);
                      updateSection("education", updated);
                    }}
                    className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Supprimer</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB CONTENT: Langues */}
      {activeTab === "languages" && (
        <div className="bg-[#14183a]/90 border border-slate-700/60 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-white">Langues</h2>
            <button
              onClick={() => {
                const newLang = { name: "Langue", level: "Courant" };
                updateSection("languages", [...cvData.languages, newLang]);
              }}
              className="px-3 py-1.5 rounded-lg bg-violet-600/20 border border-violet-500/30 text-violet-300 hover:bg-violet-600/30 text-xs font-medium flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Ajouter une langue</span>
            </button>
          </div>

          <div className="space-y-3">
            {cvData.languages.map((lang, index) => {
              const langName = typeof lang === "string" ? lang : lang.name;
              const langLevel = typeof lang === "string" ? "Courant" : (lang.level || "Courant");
              return (
                <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                  <input
                    type="text"
                    value={langName}
                    onChange={(e) => {
                      const copy = [...cvData.languages];
                      copy[index] = { name: e.target.value, level: langLevel };
                      updateSection("languages", copy);
                    }}
                    className="flex-1 px-3 py-1.5 rounded-lg bg-slate-950/80 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={langLevel}
                    onChange={(e) => {
                      const copy = [...cvData.languages];
                      copy[index] = { name: langName, level: e.target.value };
                      updateSection("languages", copy);
                    }}
                    placeholder="Niveau (ex: Bilingue, C1...)"
                    className="w-48 px-3 py-1.5 rounded-lg bg-slate-950/80 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
                  />
                  <button
                    onClick={() => {
                      const updated = cvData.languages.filter((_, i) => i !== index);
                      updateSection("languages", updated);
                    }}
                    className="text-red-400 hover:text-red-300 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Confirmation Modal ("J'ai terminé") */}
      {showCompleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="max-w-md w-full bg-[#14183a] border border-slate-700 rounded-2xl p-6 shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-white">Confirmer la fin d'édition ?</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              En confirmant, vous déclarez que vos modifications sont prêtes. Votre verrou d'accès sera
              libéré et le document sera transmis pour finalisation à l'équipe Lenvia.
            </p>
            <div>
              <label className="block text-xs text-slate-400 mb-1">
                Message pour l'équipe recrutement (optionnel)
              </label>
              <textarea
                rows={3}
                value={completeMessage}
                onChange={(e) => setCompleteMessage(e.target.value)}
                placeholder="Ex: J'ai mis à jour mes dernières certifications et mon résumé..."
                className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none leading-relaxed"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowCompleteModal(false)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium"
              >
                Continuer à modifier
              </button>
              <button
                onClick={handleComplete}
                disabled={isCompleting}
                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
              >
                {isCompleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                <span>Confirmer et clore</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
